import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { contactPositionOptions } from '@shared/constants/contact-position-options';
import { contactRequirementsByGroupTypes } from '@shared/constants/contact-requirements-by-group-types';
import { regexList } from '@shared/constants/regex-list';
import { Country } from '@shared/models/country';
import { contactRoles } from '@shared/constants/contact-roles';
import { ContactRole } from '@shared/types/contact-role';
import { GroupType } from '@shared/types/group-type';
import { UserType } from '@shared/types/user-type';
import { notUniqueValidator } from '@shared/validators/not-unique.validator';
import { ToastService } from '@shared/services/toast.service';

@Component({
  selector: 'app-add-edit-contact',
  templateUrl: './add-edit-contact.component.html',
  styleUrl: './add-edit-contact.component.scss',
  standalone: false,
})
export class AddEditContactComponent implements OnInit {
  @Input() groupType: GroupType;
  @Input() contactForm: FormGroup;
  @Input() country: Country;
  @Input() errors: any;
  @Input() availableContactRoles: ContactRole[];
  @Input() userType: UserType;
  @Input() isEdit: boolean = false;
  @Input() defaultRole?: ContactRole;

  newContactForm: FormGroup;
  contactPositionOptions = contactPositionOptions;
  showEmailDuplicate = false;
  emailError: string;
  contactRequirementsByGroupType = contactRequirementsByGroupTypes[0];
  contactRoles: typeof contactRoles;

  constructor(
    private modalController: ModalController,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.contactRequirementsByGroupType = contactRequirementsByGroupTypes.find(
      (groupTypeData) => groupTypeData.groupType === this.groupType,
    )!;

    this.initializeForm();

    this.contactRoles = contactRoles.filter(
      (contactRole) =>
        this.availableContactRoles.includes(contactRole.reference) ||
        (this.contactForm?.controls['contact_roles']?.value.includes(contactRole.reference) && this.isEdit),
    );
  }

  private initializeForm() {
    this.emailError = this.errors?.contacts?.find(
      (error: any) => error.email == this.contactForm?.controls['email']?.value,
    )?.message;

    this.newContactForm = new FormGroup({
      name: new FormControl(undefined, Validators.required),
      email: new FormControl(
        undefined,
        this.emailError!
          ? [
              Validators.required,
              Validators.pattern(regexList.is_email),
              notUniqueValidator(this.contactForm?.controls['email'].value),
            ]
          : [Validators.required, Validators.pattern(regexList.is_email)],
      ),
      contact_position: new FormControl(
        undefined,
        this.groupType !== 'customer' && this.userType != 'intranet' ? Validators.required : null,
      ),
      telephone_number: new FormControl(
        undefined,
        this.groupType !== 'customer' && this.userType != 'intranet' ? Validators.required : null,
      ),
      mobile_number: new FormControl(undefined),
      contact_roles: new FormControl(this.defaultRole ? [this.defaultRole] : [], Validators.required),
      confirmed_email: new FormControl(undefined),
    });

    if (this.isEdit) {
      this.newContactForm.controls['email'].setValidators(this.contactForm.controls['email'].validator);
      this.newContactForm.patchValue(this.contactForm.value);
      this.newContactForm.markAllAsTouched();
    }

    this.showEmailDuplicate =
      this.newContactForm.controls['email'].errors?.['notUnique'] &&
      !this.newContactForm.controls['email'].errors?.['required'];
  }

  closeModal() {
    this.modalController.dismiss({});
  }

  toggleContactRole(contactRole: ContactRole) {
    const contactRoles = [...this.newContactForm.controls['contact_roles'].value];
    if (contactRoles.includes(contactRole)) {
      contactRoles.splice(contactRoles.indexOf(contactRole), 1);
    } else {
      contactRoles.push(contactRole);
    }
    this.newContactForm.controls['contact_roles'].setValue(contactRoles);
  }

  saveContact() {
    this.newContactForm.markAllAsTouched();
    if (this.newContactForm.invalid) return;

    if (
      !this.country?.attributes?.dial_code &&
      (this.newContactForm.controls['telephone_number'].value || this.newContactForm.controls['mobile_number'].value)
    ) {
      this.toastService.showToast('error', 'Please ensure that a country is selected in Billing Details section.');
      return;
    }

    this.modalController.dismiss({ contactForm: this.newContactForm });
  }

  removeContact() {
    this.modalController.dismiss({ removeContact: true });
  }
}
